import React from 'react';
import { Spinner } from 'react-bootstrap';
import { Badge } from 'react-bootstrap';
import { FaFileContract } from 'react-icons/fa';
import { useDetailSort } from '../hooks/useDetailSort';
import { ThSort, DetailPager, DetailSectionHeader } from '../shared/index';
import { detailSectionCard } from '../utils/constants';
import { fmt, fmtDate } from '../utils/helpers';
import { exportExcel, rowsToHtml, copyHtmlToClipboard } from '../utils/exports';

const cols = [
  { key: 'id',          label: 'Contrat' },
  { key: 'dateReserv',  label: 'Date réserv.' },
  { key: 'numDoss',     label: 'N° Dossier' },
  { key: 'description', label: 'Description' },
  { key: 'projet',      label: 'Projet' },
  { key: 'type',        label: 'Type' },
  { key: 'tranche',     label: 'Tranche' },
  { key: 'superficie',  label: 'Sup. m²',  right: true },
  { key: 'prixM',       label: 'Prix/m²',  right: true },
  { key: 'prixNet',     label: 'Prix Net',  right: true },
  { key: 'part',        label: 'Part',      right: true },
  { key: 'commercial',  label: 'Commercial' },
  { key: 'clients',     label: 'Client(s)' },
];

const DetailTableVentes = ({ data, loading, open, setOpen }) => {
  const { visible, sortKey, asc, handleSort, page, setPage, totalPages, total } = useDetailSort(data);

  return (
    <div style={detailSectionCard}>
      <DetailSectionHeader
        title="Détail Ventes (CA Réalisé)" icon={<FaFileContract size={13} />}
        color="#f5c59a" count={data.length} loading={loading} open={open} setOpen={setOpen}
        onExport={() => exportExcel(data.map(r => ({
          'ID Contrat': r.id, 'Date Réserv.': r.dateReserv, 'N° Dossier': r.numDoss,
          'Description': r.description, 'Projet': r.projet, 'Type': r.type, 'Tranche': r.tranche,
          'Superficie': r.superficie, 'Prix/m²': r.prixM, 'Prix Net': r.prixNet,
          'Part': r.part != null ? `${r.part}%` : '', 'Commercial': r.commercial, 'Client(s)': r.clients,
        })), 'detail-ventes')}
        onCopy={() => { const { html, plain } = rowsToHtml(data.map(r => ({
          'ID': r.id, 'Date': fmtDate(r.dateReserv), 'N° Doss': r.numDoss || '',
          'Description': r.description || '', 'Projet': r.projet, 'Type': r.type || '',
          'Tranche': r.tranche || '', 'Prix Net': fmt(r.prixNet),
          'Part': r.part != null ? `${r.part}%` : '', 'Commercial': r.commercial || '', 'Clients': r.clients || '',
        })), 'brown'); copyHtmlToClipboard(html, plain); }}
      />
      {open && (
        <>
          {loading ? (
            <div style={{ padding: 30, textAlign: 'center' }}><Spinner /></div>
          ) : data.length === 0 ? (
            <div style={{ padding: 30, textAlign: 'center', color: '#aaa', fontSize: '0.85rem' }}>Aucune vente sur la période</div>
          ) : (
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.78rem' }}>
                <thead><tr>{cols.map(c => <ThSort key={c.key} label={c.label} field={c.key} sortKey={sortKey} asc={asc} onSort={handleSort} right={c.right} />)}</tr></thead>
                <tbody>
                  {visible.map((row, i) => (
                    <tr key={row.id} style={{ borderBottom: '1px solid #f0ebe4', background: i % 2 === 0 ? '#fff' : '#fdfaf7' }}
                      onMouseEnter={e => e.currentTarget.style.background = '#f9f3ed'}
                      onMouseLeave={e => e.currentTarget.style.background = i % 2 === 0 ? '#fff' : '#fdfaf7'}>
                      <td style={{ padding: '5px 8px', whiteSpace: 'nowrap' }}>
                        <a href={`https://shems.ngrok.io/consultation/${row.id}`} target="_blank" rel="noreferrer"
                          style={{ color: '#6d4e36', fontWeight: 700, textDecoration: 'none', borderBottom: '1px dashed #c8a882' }}>#{row.id}</a>
                      </td>
                      <td style={{ padding: '5px 8px', whiteSpace: 'nowrap' }}>{fmtDate(row.dateReserv)}</td>
                      <td style={{ padding: '5px 8px', color: '#6d4e36', fontWeight: 600 }}>{row.numDoss || '—'}</td>
                      <td style={{ padding: '5px 8px', maxWidth: 180, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{row.description || '—'}</td>
                      <td style={{ padding: '5px 8px', fontWeight: 500 }}>{row.projet}</td>
                      <td style={{ padding: '5px 8px' }}>
                        {row.rowType === 'association'
                          ? <Badge bg="none" style={{ background: '#e3f2fd', color: '#1565c0', border: '1px solid #90caf9', fontSize: '0.65rem' }}>Association</Badge>
                          : <Badge bg="none" style={{ background: '#f9f5f1', color: '#6d4e36', border: '1px solid #e8ddd4', fontSize: '0.65rem' }}>{row.type || '—'}</Badge>}
                      </td>
                      <td style={{ padding: '5px 8px', color: '#666' }}>{row.tranche || '—'}</td>
                      <td style={{ padding: '5px 8px', textAlign: 'right' }}>{row.superficie != null ? fmt(row.superficie) : '—'}</td>
                      <td style={{ padding: '5px 8px', textAlign: 'right', color: '#555' }}>{row.prixM != null ? fmt(row.prixM) : '—'}</td>
                      <td style={{ padding: '5px 8px', textAlign: 'right', fontWeight: 700, color: row.rowType === 'association' ? '#1565c0' : '#6d4e36' }}>{fmt(row.prixNet)}</td>
                      <td style={{ padding: '5px 8px', textAlign: 'right', color: '#1565c0' }}>{row.part != null ? `${row.part}%` : '—'}</td>
                      <td style={{ padding: '5px 8px', color: '#555' }}>{row.commercial || '—'}</td>
                      <td style={{ padding: '5px 8px', color: '#444', maxWidth: 160, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{row.clients || '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <DetailPager page={page} setPage={setPage} totalPages={totalPages} total={total} />
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default DetailTableVentes;
